/**
 * Controls Manager - Gestione controlli e event listeners
 */

import { attachListener, setDataAttr } from '../utils/dom.js';

export class ControlsManager {
  constructor(shadowRoot, audioManager, config) {
    this.shadowRoot = shadowRoot;
    this.audio = audioManager;
    this.config = config;
    this.state = audioManager.state;
  }
  
  /**
   * Attach tutti gli event listeners
   */
  attachEventListeners() {
    // Play/Pause
    attachListener(this.shadowRoot, '#btn-play', 'click', () => {
      this.togglePlay();
    });
    
    // Skip backward
    attachListener(this.shadowRoot, '#btn-backward', 'click', () => {
      const { isAdPlaying } = this.state.getState();
      if (isAdPlaying) {
        console.log('⏭️ Skip backward disabilitato durante pubblicità');
        return;
      }
      this.audio.skip(-this.config.skipSeconds);
    });
    
    // Skip forward
    attachListener(this.shadowRoot, '#btn-forward', 'click', () => {
      const { isAdPlaying } = this.state.getState();
      if (isAdPlaying) {
        console.log('⏭️ Skip forward disabilitato durante pubblicità');
        return;
      }
      this.audio.skip(this.config.skipSeconds);
    });
    
    // Progress bar
    attachListener(this.shadowRoot, '#progress-bar', 'click', (e) => {
      this.handleProgressClick(e);
    });
    
    // Volume slider
    attachListener(this.shadowRoot, '#volume-slider', 'input', (e) => {
      this.handleVolumeChange(e);
    });
    
    // Mute
    attachListener(this.shadowRoot, '#btn-mute', 'click', async () => {
      // Resume AudioContext se suspended (iOS richiede user interaction)
      await this.audio.resumeAudioContext();
      
      this.audio.toggleMute();
      this.updateMuteButton();
    });
    
    // Share
    attachListener(this.shadowRoot, '#btn-share', 'click', () => {
      this.share();
    });
  }
  
  /**
   * Toggle play/pause
   */
  async togglePlay() {
    const { isPlaying, isAdPlaying } = this.state.getState();
    
    // Disabilita play/pause durante ads
    if (isAdPlaying) {
      console.log('⏸️ Play/Pause disabilitato durante pubblicità');
      return;
    }
    
    if (isPlaying) {
      this.audio.pause();
    } else {
      try {
        await this.audio.resumeAudioContext();
        await this.audio.play();
      } catch (e) {
        console.error('Errore riproduzione:', e);
      }
    }
  }
  
  /**
   * Handle progress bar click
   * @param {Event} e - Click event
   */
  handleProgressClick(e) {
    const { isAdPlaying } = this.state.getState();
    if (isAdPlaying) return; // Non seekare durante ads
    
    const progressBar = e.currentTarget;
    const rect = progressBar.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = x / rect.width;
    
    this.audio.seek(percentage);
  }
  
  /**
   * Handle volume change
   * @param {Event} e - Input event
   */
  async handleVolumeChange(e) {
    const newVolume = e.target.value / 100;
    
    // Resume AudioContext se suspended (iOS richiede user interaction)
    await this.audio.resumeAudioContext();
    
    this.audio.setVolume(newVolume);
    
    // Esci da mute se volume > 0
    const { isMuted } = this.state.getState();
    if (isMuted && newVolume > 0) {
      this.state.setState({ isMuted: false });
      this.updateMuteButton();
    }
  }
  
  /**
   * Aggiorna bottone mute
   */
  updateMuteButton() {
    const { isMuted } = this.state.getState();
    const container = this.shadowRoot.querySelector('.sasha-container');
    
    if (container) {
      setDataAttr(this.shadowRoot, '.sasha-container', 'data-muted', isMuted ? 'true' : null);
    }
  }
  
  /**
   * Aggiorna volume slider
   */
  updateVolumeSlider() {
    const { volume, isMuted } = this.state.getState();
    const slider = this.shadowRoot.getElementById('volume-slider');
    
    if (slider) {
      slider.value = isMuted ? 0 : volume * 100;
    }
  }
  
  /**
   * Condivisione
   */
  async share() {
    const shareData = {
      title: this.config.title,
      text: this.config.subtitle,
      url: window.location.href
    };
    
    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(window.location.href);
        // TODO: mostra notifica
      }
    } catch (e) {
      console.error('Errore condivisione:', e);
    }
  }
  
  /**
   * Disabilita controlli durante pubblicità
   */
  disableControlsForAd() {
    const controls = [
      this.shadowRoot.getElementById('btn-play'),
      this.shadowRoot.getElementById('btn-backward'),
      this.shadowRoot.getElementById('btn-forward')
    ];
    
    controls.forEach(btn => {
      if (btn) {
        btn.classList.add('sasha-disabled-during-ad');
        btn.style.pointerEvents = 'none';
        btn.style.opacity = '0.3';
        btn.style.cursor = 'not-allowed';
      }
    });
  }
  
  /**
   * Riabilita controlli dopo pubblicità
   */
  enableControlsAfterAd() {
    const controls = [
      this.shadowRoot.getElementById('btn-play'),
      this.shadowRoot.getElementById('btn-backward'),
      this.shadowRoot.getElementById('btn-forward')
    ];
    
    controls.forEach(btn => {
      if (btn) {
        btn.classList.remove('sasha-disabled-during-ad');
        btn.style.pointerEvents = '';
        btn.style.opacity = '';
        btn.style.cursor = '';
      }
    });
  }
}

